import React, { useEffect, useState } from "react";
import {
  AnimateIn,
  AnimatePop,
  Button,
  Modal,
  TickCircleIcon,
  toast,
  usePreviousValue,
} from "@picklebet/ui";
import { Options } from "@/features/betslip/components/overlays/QuickPreferencesOptions";
import { triggerHaptic } from "@/utilities/haptics";
import { useIsLoggedIn } from "@/features/auth/hooks/useIsLoggedIn";
import { useShellStore } from "@/features/shell/stores/shellStore";
import {
  type AcceptOddsPreferences,
  usePreferences,
} from "hooks/firestore/user/usePreferences";
import { bettingPreferencesService } from "services/betting-preferences";
import { useBetslipStore } from "@/features/betslip/stores/betslipStore";

import * as styles from "@/features/betslip/components/overlays/QuickPreferences.module.scss";

const OPTIONS: AcceptOddsPreferences[] = ["AUTO", "AUTO_HIGHER", "MANUAL"];

const optionLabel = (option: AcceptOddsPreferences) => {
  switch (option) {
    case "MANUAL":
      return "Manually accept all odds changes";
    case "AUTO":
      return "Auto accept all odds changes";
    case "AUTO_HIGHER":
    default:
      return "Auto accept higher odds changes";
  }
};

export const QuickPreferences = () => {
  const { preferences, isLoading } = usePreferences();
  const isLoggedIn = useIsLoggedIn();
  const setModal = useShellStore((s) => s.setModal);
  const acceptAllOddsChanges = useBetslipStore((s) => s.acceptAllOddsChanges);
  const setOverlay = useBetslipStore((s) => s.setOverlay);
  const isOpen = useBetslipStore((s) => s.overlay.type === "quickPreferences");
  const prevIsOpen = usePreviousValue(isOpen);

  const [selected, setSelected] = useState<AcceptOddsPreferences | null>(null);
  const [saveComplete, setSaveComplete] = useState(false);

  const syncSelectionToSaved = () => {
    if (isLoading || !preferences.acceptOdds) return;
    setSelected(preferences.acceptOdds);
  };

  useEffect(() => {
    if (isOpen && prevIsOpen === false) {
      setSaveComplete(false);
      // drop a selection that was changed but never saved
      syncSelectionToSaved();
    }
  }, [isOpen, prevIsOpen]);

  useEffect(() => {
    syncSelectionToSaved();
  }, [isLoading, preferences.acceptOdds]);

  const handleClose = () => {
    setOverlay({ type: "none" });
  };

  const handleUpdate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (saveComplete) return;
    triggerHaptic.click();

    if (!isLoggedIn) {
      setModal("signIn");
      return;
    }

    const result = await bettingPreferencesService.updateBettingPreferences({
      acceptOdds: selected,
      oddsFormat: null,
      defaultHub: null,
      allowCancelWithdrawal: null,
    });

    if (!result.ok) {
      toast({
        title: "Error!",
        description: "Unable to update preferences",
        variant: "danger",
      });
      return;
    }

    if (result.data?.status === "SUCCESS") {
      triggerHaptic.success();
      setSaveComplete(true);
    }
  };

  useEffect(() => {
    if (!saveComplete) return;
    // After the success state, bake pending odds changes if the new
    // preference would have auto-accepted them.
    const timer = setTimeout(() => {
      if (selected && selected !== "MANUAL") acceptAllOddsChanges(selected);
      setOverlay({ type: "none" });
    }, 800);
    return () => clearTimeout(timer);
  }, [saveComplete, selected, acceptAllOddsChanges, setOverlay]);

  const optionsForList = OPTIONS.map((option) => ({
    value: option,
    display: <div className={styles.optionLabel}>{optionLabel(option)}</div>,
  }));

  const updateButtonVariant = !isLoggedIn ? "primary" : "info";
  const updateButtonText = !isLoggedIn
    ? "Log In To Update"
    : "Update Betting Preferences";

  return (
    <Modal.Root
      open={isOpen}
      onOpenChange={(open: boolean) => {
        if (!open) handleClose();
      }}
    >
      <Modal.Body>
        <Modal.Header>
          <Modal.Heading>
            <Modal.Title>Update Preferences</Modal.Title>
          </Modal.Heading>
        </Modal.Header>
        <Modal.Content>
          <Options
            options={optionsForList}
            setSelectedOption={setSelected}
            selectedOption={
              selected && OPTIONS.includes(selected) ? selected : OPTIONS[0]
            }
          />
          <Button
            variant={saveComplete ? "primary" : updateButtonVariant}
            className={styles.button}
            onClick={handleUpdate}
            size="md"
            data-saved={saveComplete}
            data-testid="pref-save"
          >
            {saveComplete ? (
              <AnimateIn>
                <div className={styles.savedButton}>
                  <div>Saved</div>
                  <div>
                    <AnimatePop delay={0.05}>
                      <TickCircleIcon width="1.25rem" height="1.25rem" />
                    </AnimatePop>
                  </div>
                </div>
              </AnimateIn>
            ) : (
              updateButtonText
            )}
          </Button>
        </Modal.Content>
      </Modal.Body>
    </Modal.Root>
  );
};
